import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Input, notification, Tag } from 'antd'
import { SaveOutlined } from '@ant-design/icons'

import { useAuth } from '@/middlewares/auth/auth-context'
import { getCurrentUser, userUpdate } from '@/middlewares/services/user.services'
import Stripe from '@/components/stripe/Stripe'
import LoadingScreen from '@/components/common/LoadingScreen'
import CurrencyInput from '@/components/common/CurrencyInput'
import { HandleUserOrganisationSettingsChange } from '../index.utils'
import TranslationNS from '@/utils/Constants/TranslationNS'
import PhoneNumberInput from '@/components/common/PhoneNumberInput'
import SButton from '@/components/common/SButton'
import AccountPageHeader from '../AccountPageHeader'

import CountryInput from '@/components/common/CountryInput'
import { EMPTY_INVOICE_ADDRESS, parseInvoiceAddress } from '@/utils/invoiceAddress'

import '../MyAccount.scss'

const { Title } = Typography

export default function OrganisationDetails({
    isModal = false,
    setCompleteAccount = () => {},
    setCreateCompetition = () => {},
}) {
    const { t } = useTranslation(TranslationNS.MY_ACCOUNT)

    const authContext = useAuth()

    const [user, setUser] = useState()

    // { street_number, city, country, zip_code } (Modif/621); a legacy "street, city, country" string
    // stored by the old form prefills street, city and country (utils/invoiceAddress)
    const [address, setAddress] = useState({ ...EMPTY_INVOICE_ADDRESS })
    const [phoneNumber, setPhoneNumber] = useState()

    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        const getUser = async () => {
            const response = await getCurrentUser()
            const userFound = response.data.data

            if (userFound) {
                setUser(userFound)
                if (userFound.organisation_settings) {
                    if (userFound.organisation_settings.invoice_address) {
                        setAddress(parseInvoiceAddress(userFound.organisation_settings.invoice_address))
                    }
                }

                setPhoneNumber(userFound.organisation_settings?.phone_number)

                authContext.refreshStripeAccountStatus()
            } else {
                notification.error({
                    message: t('Oops, an error occurred !'),
                    duration: 0,
                })
            }
        }
        if (authContext.user.id) {
            getUser()
        }
    }, [authContext?.user])

    const saveOrganisationProfile = async () => {
        if (!user.organisation) {
            notification.error({
                message: t('Please enter your organisation name'),
                duration: 0,
            })
        } else if (!phoneNumber?.phone) {
            notification.error({
                message: t('Please enter your phone number'),
                duration: 0,
            })
        } else if (!address.street_number || !address.city || !address.country || !address.zip_code) {
            notification.error({
                message: t('Please enter your full invoice address'),
                duration: 0,
            })
        } else {
            let newUser = { ...user }

            if (!newUser.organisation_settings) newUser.organisation_settings = {}

            newUser.organisation_settings.invoice_address = address
            newUser.organisation_settings.phone_number = phoneNumber

            setIsLoading(true)
            try {
                await userUpdate(authContext.user.id, {
                    organisation: {
                        name: newUser.organisation,
                        settings: newUser.organisation_settings,
                    },
                })
                authContext.updateUser(newUser)
                notification.success({ message: t('Informations updated !') })
            } catch (error) {
                console.error('/saveOrganisationProfile - error', error)
                notification.error({
                    message: error?.response?.data?.message || t('Oops, an error occurred !'),
                    duration: 0,
                })
            }
            if (isModal) {
                setCreateCompetition(true)
                setCompleteAccount(false)
            }
            setIsLoading(false)
        }
    }

    const HandleUserChange = (field, { target: { value } }) => {
        setUser((previous) => ({
            ...previous,
            [field]: value,
        }))
    }

    if (!user) {
        return <LoadingScreen />
    }

    return (
        <div key={user._id} className="form-account" style={isModal ? { marginInline: 'auto' } : undefined}>
            {(!user || isLoading) && <LoadingScreen />}
            {isModal ? (
                <Title level={3} className="mb-1 mt-3">
                    {t('ORGANIZER DETAILS')}
                </Title>
            ) : (
                <AccountPageHeader title={t('Organizer details')} />
            )}
            <div>
                <label>
                    {t('Organisation name')} <span className="required-indicator">*</span>
                </label>
                <Input
                    value={user.organisation}
                    onChange={(e) => HandleUserChange('organisation', e)}
                    placeholder={t('Organisation')}
                    required
                />
            </div>
            <div>
                <label>
                    {t('Phone number')} <span className="required-indicator">*</span>
                </label>
                <PhoneNumberInput value={phoneNumber} onChange={setPhoneNumber} />
            </div>

            <Title level={4} className="mt-2" style={{ marginBottom: 0 }}>
                {t('Address')}
            </Title>
            <div>
                <label>
                    {t('Street and number')} <span className="required-indicator">*</span>
                </label>
                <Input
                    value={address.street_number}
                    onChange={(e) => {
                        setAddress({
                            ...address,
                            street_number: e.target.value.replace(',', ''),
                        })
                    }}
                    placeholder={t('Street')}
                    required
                />
            </div>
            <div>
                <label>
                    {t('City')} <span className="required-indicator">*</span>
                </label>
                <Input
                    value={address.city ? address.city : ''}
                    onChange={(e) => {
                        setAddress({
                            ...address,
                            city: e.target.value.replace(',', ''),
                        })
                    }}
                    placeholder={t('City')}
                    required
                />
            </div>
            <div>
                <label>
                    {t('Country')} <span className="required-indicator">*</span>
                </label>
                <CountryInput
                    value={address.country}
                    onChange={(value) => {
                        setAddress({
                            ...address,
                            country: value,
                        })
                    }}
                    className="country-details-editor-input"
                    alwaysOpen
                />
            </div>
            <div>
                <label>
                    {t('Zip code')} <span className="required-indicator">*</span>
                </label>
                <Input
                    value={address.zip_code}
                    onChange={(e) => {
                        setAddress({
                            ...address,
                            zip_code: e.target.value.replace(',', ''),
                        })
                    }}
                    placeholder={t('Zip code')}
                    required
                />
            </div>
            <div>
                <Title level={4} className="mt-2">
                    <img
                        src="https://b.stripecdn.com/manage-statics-srv/assets/public/favicon.ico"
                        style={{ width: '18px' }}
                    />{' '}
                    {t('Stripe')}{' '}
                    {user.organisation_settings?.stripe?.account_id && <Tag color="green">{t('connected')}</Tag>}
                </Title>
                {authContext.stripeAccountStatus && (
                    <div style={{ padding: '0 1em 1em 1em' }}>
                        <h3>
                            {t('status')}:{' '}
                            <Tag color={authContext.stripeAccountStatus.color}>
                                {authContext.stripeAccountStatus.label}
                            </Tag>
                        </h3>
                        <p>{authContext.stripeAccountStatus.reason}</p>
                    </div>
                )}
            </div>
            {!user.organisation_settings?.stripe && (
                <p className="mb-1">
                    {t(
                        'Stripe is our payment partner. You can connect or create an account now to accept payments. You also can do it later.',
                    )}
                </p>
            )}
            <Stripe user={user} />

            {user.organisation_settings?.stripe?.account_id && (
                <>
                    <div>
                        <label>
                            {t('Currency')} <span className="required-indicator">*</span>
                        </label>
                        <CurrencyInput
                            currency={user.organisation_settings.currency}
                            stripe_account_id={user.organisation_settings.stripe.account_id}
                            onChange={(value) => HandleUserOrganisationSettingsChange('currency', value, setUser)}
                        />
                    </div>
                </>
            )}
            <Title level={4}>{t('Billing information')}</Title>
            <div>
                <label>{t('VAT number (not required)')}</label>
                <Input
                    value={user.organisation_settings && user.organisation_settings.VAT_number}
                    onChange={(e) => HandleUserOrganisationSettingsChange('VAT_number', e.target.value, setUser)}
                    placeholder={t('VAT number')}
                />
            </div>
            <div>
                <label>{t('Tax rate in your country % (not required)')}</label>
                <Input
                    suffix="%"
                    type="number"
                    value={user.organisation_settings && user.organisation_settings.taxe_rate}
                    onChange={(e) => HandleUserOrganisationSettingsChange('taxe_rate', e.target.value, setUser)}
                    placeholder={t('Tax rate')}
                />
            </div>

            {isModal ? (
                <div className="" style={{ borderRadius: '8px', marginTop: '20px' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.5em',
                        }}
                    >
                        <SButton value={t('Cancel')} onClick={() => setCompleteAccount(false)} outlined />
                        <SButton value={t('Next')} onClick={saveOrganisationProfile} />
                    </div>
                </div>
            ) : (
                <div>
                    <SButton
                        value={t('Save')}
                        onClick={saveOrganisationProfile}
                        Icon={SaveOutlined}
                        iconStyle={{ fontSize: '1em' }}
                        style={{ marginLeft: 'auto' }}
                    />
                </div>
            )}
        </div>
    )
}
